import { Targets } from '@atlassiansox/cross-flow-support';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { JSW_SPRINT_PLANNING_WHITEBOARDS_ENTRYPOINT } from '../../feature-flags.tsx';
import { BACKLOG } from '../../model/constants.tsx';
import { useBacklogSelector } from '../../state/index.tsx';
import { getOrderedCardIdsByCardListId } from '../../state/selectors/cards/index.tsx';
import {
	getHasConfluenceAccess,
	getHasConfluenceSpaces,
} from '../../state/selectors/whiteboards-entrypoint-experiment/index.tsx';
import type { WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS } from './constants.tsx';

// Checks if the number of issues in the backlog is greater than 2
const useIsBacklogCardListSizeGreaterThanTwo = (): boolean =>
	useBacklogSelector(getOrderedCardIdsByCardListId)[BACKLOG]?.length > 2;

// Checks it the tenant has Confluence site
const useTenantHasConfluence = (): boolean => {
	const { licensedProducts } = useTenantContext();
	return licensedProducts?.confluence === true;
};

const useTenantHasProductSuggestionsEnabled = (): boolean => {
	const { areProductSuggestionsEnabled } = useTenantContext();
	return areProductSuggestionsEnabled;
};

// Crossflow eligibile if the tenant does not have Confluence site and the user is an admin
export const useIsCrossFlowEligible = (): boolean => {
	const isAdmin = useIsAdmin();
	const hasConfluence = useTenantHasConfluence();
	return useTenantHasProductSuggestionsEnabled() && !hasConfluence && isAdmin;
};

// Co-use eligible if the tenant has Confluence site and the user has access to that Confluence site
export const useIsCoUseEligible = (): boolean => {
	const hasAccess = useBacklogSelector(getHasConfluenceAccess);
	const hasSpaces = useBacklogSelector(getHasConfluenceSpaces);
	return useTenantHasConfluence() && hasAccess && hasSpaces;
};

export const useFireExposureEvents = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const crossFlowEligible = useIsCrossFlowEligible();
	const coUseEligible = useIsCoUseEligible();
	const { fireViewedScreenEvent } = useFireCrossFlowAnalyticsEvents();

	const fireWhiteboardsExposureEvents = (
		cohort: WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS | string | null,
	) => {
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'feature exposed',
			'jswSprintPlanningWhiteboardsExperiment',
			{
				flagKey: JSW_SPRINT_PLANNING_WHITEBOARDS_ENTRYPOINT,
				cohort,
				crossFlowEligible,
				coUseEligible,
			},
		);

		// xflow UCIA events
		if (cohort === 'variant_contextual_menu') {
			fireViewedScreenEvent('planOnWhiteboardsContextualCTAScreen', {
				recommendedProductIds: [Targets.CONFLUENCE],
			});
		} else if (cohort === 'variant_backlog_button') {
			fireViewedScreenEvent('planOnWhiteboardsButtonCTAScreen', {
				recommendedProductIds: [Targets.CONFLUENCE],
			});
		} else if (cohort === 'control') {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'viewed',
					actionSubject: 'component',
				}),
				'planOnWhiteboardsControlScreen',
			);
		}
	};

	return fireWhiteboardsExposureEvents;
};

// Eligiblity for whiteboards experiment based on being coUse or crossFlow eligible and if no. of issues in backlog > 2
export const useGetWhiteboardEligibleFlow = (): 'crossFlow' | 'coUse' | null => {
	const isCrossFlowEligible = useIsCrossFlowEligible();
	const isCoUseEligible = useIsCoUseEligible();
	const backlogCardListSizeGreaterThanTwo = useIsBacklogCardListSizeGreaterThanTwo();
	if (isCrossFlowEligible && backlogCardListSizeGreaterThanTwo) {
		return 'crossFlow';
	}
	if (isCoUseEligible && backlogCardListSizeGreaterThanTwo) {
		return 'coUse';
	}
	return null;
};
